import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Callout, Dialog, Illustration, Input, toast } from '@node-space/ui-components'
import { useUpdateCardStatusMutation } from 'hooks/mutations/Cards/useUpdateCardStatusMutation'
import { Card } from 'services/CardService'

interface FreezeCardDialogProps {
  card: Card
  open: boolean
  onClose: () => void
}

export const CustomerCardFreezeDialog: React.FC<FreezeCardDialogProps> = ({
  card,
  open,
  onClose,
}) => {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')

  const isFrozen = card.status === 'FROZEN'
  const translationKey = isFrozen ? 'unfreezeDialog' : 'freezeDialog'

  const {
    mutate: updateCardStatus,
    isPending,
    isError,
    error,
    reset,
  } = useUpdateCardStatusMutation()

  const handleClose = () => {
    setReason('')
    reset()
    onClose()
  }

  const handleConfirm = () => {
    updateCardStatus(
      {
        cardId: card.id,
        newStatus: isFrozen ? 'ACTIVE' : 'FROZEN',
        reason: reason.trim(),
      },
      {
        onSuccess: () => {
          toast({
            header: t(`customers.cardDetails.${translationKey}.successTitle`),
            body: t(`customers.cardDetails.${translationKey}.successDescription`),
            variant: 'success',
          })
          handleClose()
        },
      }
    )
  }

  if (!open) return null

  return (
    <Dialog
      open
      title={t(`customers.cardDetails.${translationKey}.title`)}
      onClose={handleClose}
      Illustration={<Illustration name="Warning" />}
      ActionButtons={[
        <Button
          key="confirm"
          testId="freeze-card-confirm"
          loading={isPending}
          onClick={handleConfirm}
        >
          {t(`customers.cardDetails.${translationKey}.confirm`)}
        </Button>,
        <Button
          key="cancel"
          variant="secondary"
          testId="freeze-card-cancel"
          onClick={handleClose}
          disabled={isPending}
        >
          {t('cancel')}
        </Button>,
      ]}
    >
      <Box flexCol gapY={16}>
        <p className="text-foreground-prominent text-body-regular text-center">
          {t(`customers.cardDetails.${translationKey}.description`)}
        </p>
        <Input
          testId="freeze-card-reason"
          label={t('customers.cardDetails.freezeDialog.reasonLabel')}
          value={reason}
          onChange={event => setReason(event.target.value)}
          disabled={isPending}
        />
        {isError && (
          <Callout variant="error" testId="freeze-card-error">
            {t(`customers.cardDetails.${translationKey}.errorMessage`)}
            {!!error?.data?.traceId && (
              <p className="text-body-regular-small text-foreground-subtle pt-2">
                {t('errorMessages.traceId')}: {error.data.traceId}
              </p>
            )}
          </Callout>
        )}
      </Box>
    </Dialog>
  )
}
